"use client";

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";

import { Label } from "@/components/ui/label";
import { Tag, XCircle, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { showSuccess, showError } from "@/utils/toast";

interface TagObject {
  id: string;
  name_hu: string;
  name_en: string;
  adult: boolean;
  search_term?: string; // A kereséshez használt, mindkét nyelvet tartalmazó string
}

interface GameAdminTagsPanelProps {
  gameId: number;
  initialTags: TagObject[];
}

const GameAdminTagsPanel: React.FC<GameAdminTagsPanelProps> = ({ gameId, initialTags }) => {
  const [currentTags, setCurrentTags] = useState<TagObject[]>(initialTags || []);
  const [allAvailableTags, setAllAvailableTags] = useState<TagObject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

  // Összes elérhető tag betöltése
  React.useEffect(() => {
    const fetchAllTags = async () => {
      try {
        // A limit=0 egy trükk lehet, hogy minden taget lekérjünk, ha az API támogatja.
        // Vagy egy nagyon magas limitet állítunk be.
        const response = await fetch('/api/tags?limit=1000');
        if (!response.ok) throw new Error('Nem sikerült betölteni a tageket.');
        const data = await response.json();
        const tagsWithSearchTerm = data.map((tag: TagObject) => ({
          ...tag,
          search_term: `${tag.name_en} ${tag.name_hu || ''}`.toLowerCase(),
        }));
        setAllAvailableTags(tagsWithSearchTerm);
      } catch (error) {
        showError(error instanceof Error ? error.message : 'Ismeretlen hiba');
      }
    };
    fetchAllTags();
  }, []);

  const handleRemoveTag = (tagToRemove: TagObject) => {
    setCurrentTags(prev => prev.filter(tag => tag.id !== tagToRemove.id));
  };

  const handleToggleTag = (tagToToggle: TagObject, onSelectCallback: () => void) => {
    setCurrentTags(prev =>
      prev.some(tag => tag.id === tagToToggle.id)
        ? prev.filter(tag => tag.id !== tagToToggle.id)
        : [...prev, tagToToggle]
    );
    onSelectCallback();
  };

  const handleSaveTags = async () => {
    try {
      setIsLoading(true);
      const tagIds = currentTags.map(tag => tag.id);
      const response = await fetch(`/api/games/${gameId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ game: { tag_ids: tagIds } }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(', ') || 'Mentés sikertelen');
      }

      showSuccess('A tagek sikeresen frissítve!');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Ismeretlen hiba');
    } finally {
      setIsLoading(false);
    }
  };

  const adultTags = allAvailableTags.filter(tag => tag.adult);

  return (
    <Card className="bg-card text-card-foreground border-border shadow-md">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
          <Tag size={20} /> Játék címkék
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-muted-foreground block mb-2">Jelenlegi tagek:</Label>
          <div className="flex flex-wrap gap-2">
            {currentTags.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nincsenek hozzárendelt címkék.</p>
            ) : (
              currentTags.map(tag => (
                <Badge
                  key={tag.id}
                  className="bg-green-700 text-green-100 border border-green-500 flex items-center gap-1 pr-1 cursor-pointer"
                  onClick={() => handleRemoveTag(tag)}
                >
                  {tag.name_hu || tag.name_en} <XCircle size={12} className="ml-1" />
                </Badge>
              ))
            )}
          </div>
        </div>

        {adultTags.length > 0 && (
          <div>
            <Label className="text-muted-foreground block mb-2">Gyakori (18+) tagek:</Label>
            <div className="flex flex-wrap gap-2">
              {adultTags.map(tag => (
                <Badge
                  key={`adult-${tag.id}`}
                  variant={currentTags.some(t => t.id === tag.id) ? "default" : "secondary"}
                  className="cursor-pointer"
                  onClick={() => handleToggleTag(tag, () => { })}
                >
                  {tag.name_hu || tag.name_en}
                </Badge>
              ))}
            </div>
          </div>
        )}


        <div>
          <Label className="text-muted-foreground block mb-2">Címke hozzáadása:</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between bg-secondary text-secondary-foreground border-border hover:bg-muted hover:text-muted-foreground"
              >
                Válassz a címkék közül
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-card text-card-foreground border-border">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Címke keresése..."
                  value={searchInput}
                  onValueChange={setSearchInput}
                />
                <CommandEmpty>Nincs ilyen címke.</CommandEmpty>
                <CommandGroup>
                  <ScrollArea className="h-48">
                    {allAvailableTags.filter(tag => tag.search_term?.includes(searchInput.toLowerCase())).map((tag) => (
                      <CommandItem
                        key={tag.id}
                        value={tag.search_term}
                        onSelect={() => handleToggleTag(tag, () => setSearchInput(''))}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            currentTags.some(t => t.id === tag.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {tag.name_en}{tag.name_hu ? ` - ${tag.name_hu}` : ''}
                      </CommandItem>
                    ))}
                  </ScrollArea>
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <Button onClick={handleSaveTags} disabled={isLoading} className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground">
          Módosítások mentése
        </Button>
      </CardContent>
    </Card>
  );
};

export default GameAdminTagsPanel;